import { useState, useEffect, useMemo, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';

export const FORM_ACTIVITY_FILTER_ID = 'form-activity-filter';

export default function FormActivityFilter({ collaborators, onSubmit, currentFilter, onReset }) {
    const { t } = useTranslation();
    const allIds = useMemo(() => collaborators.map((c) => c.id), [collaborators]);

    const toSet = useCallback(
        (filter) => {
            if (!filter || filter === 'all') return new Set(allIds);
            if (Array.isArray(filter)) return new Set(filter);
            return new Set([filter]);
        },
        [allIds],
    );

    const [selected, setSelected] = useState(() => toSet(currentFilter));

    useEffect(() => {
        if (currentFilter !== undefined) {
            setSelected(toSet(currentFilter));
        }
    }, [currentFilter, toSet]);

    useEffect(() => {
        if (onReset) {
            setSelected(new Set(allIds));
        }
    }, [onReset, allIds]);

    const isAllSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));
    const isNoneSelected = selected.size === 0;

    const handleToggle = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
                if (next.size === 0) return new Set(allIds);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(isAllSelected ? 'all' : Array.from(selected));
    };

    return (
        <form
            id={FORM_ACTIVITY_FILTER_ID}
            onSubmit={handleSubmit}
            className="flex-1 overflow-y-auto px-6 py-4 space-y-3"
        >
            {/* Boutons tout sélectionner / désélectionner */}
            {allIds.length > 1 && (
                <div className="flex gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        color="neutral"
                        size="xmd"
                        disabled={isAllSelected}
                        onClick={() => setSelected(new Set(allIds))}
                        className="flex-1 text-xs"
                    >
                        {t('account:toutSelectionner')}
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        color="neutral"
                        size="xmd"
                        disabled={isNoneSelected || selected.size === 1}
                        onClick={() => setSelected(new Set())}
                        className="flex-1 text-xs"
                    >
                        {t('account:toutDeselectionner')}
                    </Button>
                </div>
            )}

            {/* Liste des collaborateurs */}
            <div className="space-y-2">
                {collaborators.map((collab) => {
                    const isActive = selected.has(collab.id);
                    return (
                        <label
                            key={collab.id}
                            className={`flex items-center w-full px-4 py-3 rounded-lg border cursor-pointer transition-colors ${
                                isActive
                                    ? 'border-secondary bg-secondary text-secondary-foreground'
                                    : 'border-border bg-background hover:bg-surface text-foreground'
                            }`}
                        >
                            <input
                                type="checkbox"
                                value={collab.id}
                                checked={isActive}
                                onChange={() => handleToggle(collab.id)}
                                className="sr-only"
                            />
                            <span className="text-sm">{collab.name}</span>
                        </label>
                    );
                })}
            </div>
        </form>
    );
}
